// 無限ループのスライダー（中央のカードが現在地）: [data-mp-loop-slider]
//   [data-mp-loop-slider-track]   カードを並べたトラック（ビューポート要素の直下）
//   [data-mp-loop-slider-prev|next] 前後ボタン / [data-mp-loop-slider-count] 「01 / 04」の表示（aria-live）
// カード一式を前後に 1 セットずつ複製して 3 セットにし、端まで動いたら真ん中のセットへ瞬時に戻す。
// 動いたら root に .is-ready を付ける（それまでは CSS の横スクロール一覧として読める）。
// 現在地以外のカードは inert にして、フォーカスと読み上げの対象から外す。自動再生はしない。

import { reduceMotion } from './reveal.js';

const pad = (n) => String(n).padStart(2, '0');

function setupLoopSlider(root) {
  const track = root.querySelector('[data-mp-loop-slider-track]');
  const viewport = track && track.parentElement;
  const originals = track ? [...track.children] : [];
  const n = originals.length;
  if (!viewport || n < 2) return;

  const clone = (slide) => {
    const copy = slide.cloneNode(true);
    copy.removeAttribute('aria-roledescription');
    copy.removeAttribute('aria-label');
    copy.setAttribute('aria-hidden', 'true');
    copy.inert = true;
    return copy;
  };
  track.prepend(...originals.map(clone));
  track.append(...originals.map(clone));
  const slides = [...track.children];
  const count = root.querySelector('[data-mp-loop-slider-count]');

  let pos = n; // 真ん中のセットの先頭
  let dx = 0; // ドラッグ中のずれ
  let startX = null;

  const render = (animate) => {
    const width = slides[0].offsetWidth; // 縮小表示（transform）の影響を受けないレイアウト上の幅
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const x = (viewport.clientWidth - width) / 2 - pos * (width + gap) + dx;
    track.classList.toggle('is-animating', animate && !reduceMotion);
    track.style.transform = `translateX(${x}px)`;
  };

  const update = () => {
    const index = ((pos % n) + n) % n;
    slides.forEach((slide, i) => slide.classList.toggle('is-current', i === pos));
    originals.forEach((slide, i) => {
      slide.inert = i !== index;
    });
    if (count) count.textContent = `${pad(index + 1)} / ${pad(n)}`;
  };

  // 複製のセットにいるときは、見た目を変えずに真ん中のセットへ移す
  const recenter = () => {
    if (pos >= n && pos < 2 * n) return;
    pos += pos < n ? n : -n;
    render(false);
    update();
    track.getBoundingClientRect(); // 位置を確定させてから次のアニメーションを始める
  };

  const go = (step) => {
    recenter();
    pos += step;
    dx = 0;
    render(true);
    update();
    if (reduceMotion) recenter();
  };

  track.addEventListener('transitionend', (event) => {
    if (event.target === track && event.propertyName === 'transform') recenter();
  });
  root.querySelector('[data-mp-loop-slider-prev]')?.addEventListener('click', () => go(-1));
  root.querySelector('[data-mp-loop-slider-next]')?.addEventListener('click', () => go(1));

  // スワイプ（カード幅の 20%、最大 80px 動かしたら送る）
  viewport.addEventListener('pointerdown', (event) => {
    if (event.target.closest('a, button') || (event.pointerType === 'mouse' && event.button !== 0)) return;
    startX = event.clientX;
    dx = 0;
    viewport.setPointerCapture(event.pointerId);
  });
  viewport.addEventListener('pointermove', (event) => {
    if (startX === null) return;
    dx = event.clientX - startX;
    render(false);
  });
  const release = () => {
    if (startX === null) return;
    startX = null;
    const threshold = Math.min(80, slides[0].offsetWidth * 0.2);
    if (Math.abs(dx) > threshold) {
      go(dx < 0 ? 1 : -1);
    } else {
      dx = 0;
      render(true);
    }
  };
  viewport.addEventListener('pointerup', release);
  viewport.addEventListener('pointercancel', release);

  root.classList.add('is-ready');
  viewport.scrollLeft = 0; // JS が動く前に横スクロールされていた分を戻す
  render(false);
  update();
  new ResizeObserver(() => render(false)).observe(viewport);
}

export function setupLoopSliders() {
  document.querySelectorAll('[data-mp-loop-slider]').forEach(setupLoopSlider);
}
